import type {
  PreviewManualSessionState,
  SendPreviewManualTurnRequest,
} from "@/api/previewManual";

/**
 * Manual sessions: the small pure parts the Lab composer shares. No text of
 * either side is held here beyond what a caller passes in.
 */

/** The service's bound on one patient line (after trimming). */
export const MANUAL_TEXT_MAX = 4000;

export const MANUAL_STATUS_LABELS = {
  ready: "Ready",
  busy: "Liv is replying",
  ended: "Ended",
} as const satisfies Record<PreviewManualSessionState["status"], string>;

/** The line the service would receive, or null when it is blank or too long. */
export function readManualText(text: string): string | null {
  const trimmed = text.trim();
  return trimmed.length >= 1 && trimmed.length <= MANUAL_TEXT_MAX
    ? trimmed
    : null;
}

/**
 * Why the composer cannot send right now, or null when it can. `restoreThrough`
 * is the turn a replay this browser started runs to: the target can report
 * `ready` before it reports the replay, and a line sent then stops the replay.
 */
export function manualBlocker(
  state: PreviewManualSessionState,
  text: string,
  restoreThrough: number | null = null,
): string | null {
  if (state.sessionEnded || state.status === "ended") {
    return "This conversation has ended. Start a new manual session to continue.";
  }
  const replay = state.replayStatus ?? null;
  if (
    replay === "queued" ||
    replay === "running" ||
    (replay === null &&
      restoreThrough !== null &&
      state.turnsCompleted < restoreThrough)
  ) {
    return "The replayed turns are still running. You can type once they finish.";
  }
  if (state.status === "busy") {
    return "Liv is still replying to the previous line.";
  }
  if (text.trim().length > MANUAL_TEXT_MAX) {
    return `A line can be at most ${MANUAL_TEXT_MAX} characters.`;
  }
  return null;
}

/** One send attempt: the key and turn it was first made under, and the draft it carried. */
export type ManualAttempt = {
  key: string;
  expectedTurn: number;
  text: string;
};

/**
 * The attempt a send belongs to. An unchanged draft after an inconclusive
 * attempt REUSES its key and expected turn — so a turn that did land is
 * answered `replayed` rather than submitted twice. Anything else is a new
 * attempt on the session's next turn.
 */
export function nextAttempt(
  previous: ManualAttempt | null,
  text: string,
  state: PreviewManualSessionState,
  newKey: () => string = () => crypto.randomUUID(),
): ManualAttempt {
  return previous !== null && previous.text === text
    ? previous
    : { key: newKey(), expectedTurn: state.turnsCompleted + 1, text };
}

export function manualRequestBody(
  attempt: ManualAttempt,
): SendPreviewManualTurnRequest {
  return {
    text: attempt.text,
    expectedTurn: attempt.expectedTurn,
    idempotencyKey: attempt.key,
  };
}

/** The latest turn a run has recorded, for "Follow live"; null when none. */
export function latestTurnNumber(
  turns: readonly { turnNumber: number }[],
): number | null {
  return turns.reduce<number | null>(
    (latest, turn) =>
      latest === null || turn.turnNumber > latest ? turn.turnNumber : latest,
    null,
  );
}
